import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import type { Visit } from '@/store/visitsSlice';

export interface VisitAiOutput {
  summary?: string;
  issues?: string[];
  overall_class?: string;
}

export interface TaskAiAnswer {
  question: string;
  answer: 'yes' | 'no';
  comment: string;
}

export interface TaskAiOutput {
  photo_descriptions?: string[];
  audio_transcription?: string;
  answers?: TaskAiAnswer[];
  classification?: string;
}

export type TaskType = 'text_input' | 'photo' | 'checkbox' | 'action';

export interface TaskTemplateRef {
  id: string;
  slug: string;
  title: string;
  type: TaskType;
  instruction: string | null;
  position: number;
}

export interface TaskPhoto {
  id: string;
  photoableType: 'visit_template' | 'task_template' | 'task';
  photoableId: string;
  url: string;
  position: number;
  createdAt: string;
}

export type TaskStatus = 'pending' | 'submitted';

export interface VisitTask {
  id: string;
  visitId: string;
  taskTemplateId: string;
  status: TaskStatus;
  textValue: string | null;
  booleanValue: boolean | null;
  comment: string | null;
  aiOutputs: TaskAiOutput[] | null;
  submittedAt: string | null;
  template: TaskTemplateRef;
  photos: TaskPhoto[];
}

export interface VisitDetail extends Visit {
  aiOutput: VisitAiOutput | null;
  aiError: string | null;
  tasks: VisitTask[];
}

interface VisitDetailState {
  visit: VisitDetail | null;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  fetchVisit: (id: string) => Promise<void>;
  reset: () => void;
}

export const useVisitDetailStore = create<VisitDetailState>()((set) => ({
  visit: null,
  isLoading: false,
  error: null,
  notFound: false,

  fetchVisit: async (id) => {
    set({ isLoading: true, error: null, notFound: false });
    try {
      const res = await apiFetch(`/api/v1/admin/visits/${id}`);
      if (res.status === 404) {
        set({ visit: null, isLoading: false, notFound: true });
        return;
      }
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = (await res.json()) as VisitDetail;
      set({ visit: data, isLoading: false });
    } catch (e) {
      set({
        visit: null,
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load visit',
      });
    }
  },

  reset: () => set({ visit: null, isLoading: false, error: null, notFound: false }),
}));

export const selectVisitDetail = (s: VisitDetailState) => s.visit;
export const selectVisitDetailIsLoading = (s: VisitDetailState) => s.isLoading;
export const selectVisitDetailError = (s: VisitDetailState) => s.error;
export const selectVisitDetailNotFound = (s: VisitDetailState) => s.notFound;
